import { cn, HapticSurface, panelRow, useTheme } from "@pax/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AccountAvatar } from "@/features/auth/components/AccountAvatar";
import { type Session, signOut, useCurrentProfile } from "@/features/auth/queries";
import { formatCredits } from "@/features/credits/format";
import { creditBalanceQuery } from "@/features/credits/queries";
import { ProfileScreen, ProfileSkeleton } from "@/features/profiles/components/ProfileScreen";
import { profileQuery } from "@/features/profiles/queries";
import { useArrangement } from "@/shell/arrangement";
import { useLeaveYou } from "@/shell/leave-you";
import { usePhoneWidth } from "@/shell/use-phone-width";
import { AccountSection } from "./AccountSection";
import { AppearanceSection, THEMES } from "./AppearanceSection";
import { BillingSection } from "./BillingSection";
import { InvitesSection, useInvitesLeft } from "./InvitesSection";
import { NotificationsSection } from "./NotificationsSection";
import { PrivacySection } from "./PrivacySection";
import { backChrome, Group } from "./parts";
import { SETTINGS_SECTIONS, type SettingsSectionId, settingsSectionFrom } from "./sections";
import { SubPage, type SubPageHeader } from "./sub-page";

/** Wide enough for the list of sections and the open one side by side. */
const WIDE_PX = 640;

/** One curve and length for every move between pages in settings. */
const PUSH = { duration: 420, easing: "cubic-bezier(0.32, 0.72, 0, 1)" } as const;
const pushClass =
  "transition-[transform,opacity] duration-[420ms] ease-[cubic-bezier(0.32,0.72,0,1)]";

/** The page underneath, as it is pushed aside: a little to the left and a little dimmer. */
const UNDER = { transform: "translateX(-30%)", opacity: 0.6 };
const ON = { transform: "translateX(0)", opacity: 1 };
const OFF = { transform: "translateX(100%)", opacity: 1 };

const pageShadow = "shadow-[-12px_0_24px_-12px_rgb(0_0_0/0.15)]";

/** Whether the element is wide enough for both columns, measured before the first paint. */
function useWide(element: RefObject<HTMLElement | null>): boolean | null {
  const [wide, setWide] = useState<boolean | null>(null);
  useLayoutEffect(() => {
    const node = element.current;
    if (!node) return;
    const measure = () => setWide(node.getBoundingClientRect().width >= WIDE_PX);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [element]);
  return wide;
}

/** Whether the column beside the screen is standing and lists the sections. */
function useListInColumn() {
  const arrangement = useArrangement();
  const phone = usePhoneWidth();
  return !phone && arrangement === "sidebar";
}

/**
 * Settings. With the sidebar open, the column lists the sections and the screen holds the open one.
 * Otherwise a wide screen puts the list beside the open section, and a narrow one is a phone's list
 * that slides into each section; that switch is the width of the screen, not the window.
 */
export function SettingsScreen({
  session,
  section,
}: {
  session: Session;
  /** The section in the URL, or undefined at `/settings`. */
  section: SettingsSectionId | undefined;
}) {
  const root = useRef<HTMLDivElement>(null);
  const measuredWide = useWide(root);
  // With the sections already listed in the column, the screen only ever holds the open section.
  const wide = useListInColumn() || measuredWide;
  return (
    <div ref={root} className="@container/settings absolute inset-0 overflow-hidden">
      {wide === null ? null : wide ? (
        <YouFrame>
          <WideLayout session={session} section={section ?? SETTINGS_SECTIONS[0].id} />
        </YouFrame>
      ) : (
        <NarrowLayout session={session} section={section} />
      )}
    </div>
  );
}

function SectionBody({
  id,
  session,
  phone,
}: {
  id: SettingsSectionId;
  session: Session;
  phone: boolean;
}) {
  switch (id) {
    case "profile":
      return <ProfileTab userId={session.user.id} />;
    case "account":
      return <AccountSection session={session} />;
    case "billing":
      return <BillingSection userId={session.user.id} />;
    case "appearance":
      return <AppearanceSection phone={phone} />;
    case "notifications":
      return <NotificationsSection />;
    case "privacy":
      return <PrivacySection />;
    case "invites":
      return <InvitesSection />;
  }
}

/** Your profile as others see it, where each part opens its editor. */
function ProfileTab({ userId }: { userId: string }) {
  const { data: profile } = useQuery(profileQuery(userId));
  if (!profile) return <ProfileSkeleton />;
  return <ProfileScreen profile={profile} viewerId={userId} editing />;
}

/** The current value at the end of each section's row on a phone, where it has one. */
function useSectionSummaries(userId: string): Partial<Record<SettingsSectionId, string>> {
  const { data: credits } = useQuery(creditBalanceQuery(userId));
  const { choice } = useTheme();
  const invitesLeft = useInvitesLeft();
  return {
    billing: credits === undefined ? undefined : formatCredits(credits),
    appearance: THEMES.find(theme => theme.value === choice)?.label,
    invites: invitesLeft === undefined ? undefined : `${invitesLeft} left`,
  };
}

/** Signs out where you are; the route guard then sends you on to sign in. */
function useSignOut() {
  const queryClient = useQueryClient();
  const router = useRouter();
  return async () => {
    try {
      await signOut(queryClient);
    } finally {
      await router.invalidate();
    }
  };
}

const sectionLink = (id: SettingsSectionId) =>
  ({ to: "/settings/{-$section}", params: { section: id } }) as const;

/** The list of sections, with Sign out at its foot. */
function SectionList({ section }: { section: SettingsSectionId | undefined }) {
  const signOutHere = useSignOut();
  return (
    <>
      {SETTINGS_SECTIONS.map(item => (
        <Link
          key={item.id}
          {...sectionLink(item.id)}
          aria-current={item.id === section ? "page" : undefined}
          className={cn(panelRow, item.id === section && "bg-foreground/[0.07]")}
        >
          <item.icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          <HapticSurface />
        </Link>
      ))}
      <div className="mt-auto pt-4">
        <button
          type="button"
          onClick={() => void signOutHere()}
          className={cn(panelRow, "text-muted-foreground hover:text-foreground")}
        >
          <LogOut aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">Sign out</span>
          <HapticSurface />
        </button>
      </div>
    </>
  );
}

/**
 * Your face of the app's column in settings: Profile first, then each other section, and Sign out
 * at the foot. It slides over from the ways, so there is one list.
 */
export function SettingsColumn() {
  const pathname = useRouterState({ select: state => state.location.pathname });
  const inSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const section = inSettings
    ? (settingsSectionFrom(pathname.split("/")[2]) ?? SETTINGS_SECTIONS[0].id)
    : undefined;
  return (
    <nav aria-label="You" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2 pt-0">
      <SectionList section={section} />
    </nav>
  );
}

/**
 * Settings with your list beside it. In the sidebar the list is the app's column; in the top bar,
 * with room for it, it stands at the screen's left with a way back to where you came from.
 */
function YouFrame({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const wide = useWide(root);
  const arrangement = useArrangement();
  const phone = usePhoneWidth();
  const leaveYou = useLeaveYou();
  const listBeside = wide === true && !phone && arrangement === "bar";
  return (
    <div ref={root} className="flex h-full">
      {listBeside ? (
        <aside className="sticky top-0 flex h-(--screen-height) max-h-full w-[12.5rem] shrink-0 flex-col pt-2">
          <div className="px-2 pb-1">
            <button type="button" onClick={leaveYou} aria-label="Back" className={backChrome}>
              <ArrowLeft aria-hidden className="size-4" />
              <HapticSurface />
            </button>
          </div>
          <SettingsColumn />
        </aside>
      ) : null}
      <div className="relative min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Wide enough for a section beside the list, which `YouFrame` or the app's column draws. */
function WideLayout({ session, section }: { session: Session; section: SettingsSectionId }) {
  const [sub, setSub] = useState<SubPageHeader | null>(null);
  const wideApi = useMemo(() => ({ set: setSub, prepare: () => {} }), []);
  const current = SETTINGS_SECTIONS.find(item => item.id === section) ?? SETTINGS_SECTIONS[0];
  // Your profile fills the page as it does everywhere, banner and all.
  if (section === "profile") {
    return (
      <div className="h-full overflow-y-auto">
        <SectionBody id="profile" session={session} phone={false} />
      </div>
    );
  }
  return (
    <div className="scroll-fade h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl px-8 pt-8 pb-24">
        <div className="flex min-h-9 items-center gap-3">
          {sub ? (
            <button type="button" onClick={sub.onBack} aria-label="Back" className={backChrome}>
              <ChevronLeft aria-hidden className="size-4" />
              <HapticSurface />
            </button>
          ) : null}
          <h1 className="truncate text-xl font-semibold tracking-tight">
            {sub?.title ?? current.label}
          </h1>
        </div>
        <div className="mt-6">
          <SubPage value={wideApi}>
            <SectionBody key={section} id={section} session={session} phone={false} />
          </SubPage>
        </div>
      </div>
    </div>
  );
}

/** Too narrow for both: a list of sections, each sliding in over it. */
function NarrowLayout({
  session,
  section,
}: {
  session: Session;
  section: SettingsSectionId | undefined;
}) {
  const navigate = useNavigate();
  const signOutHere = useSignOut();
  const summaries = useSectionSummaries(session.user.id);
  const profile = useCurrentProfile(session.user);
  const displayName = profile.data?.displayName ?? "Account";
  const [sub, setSub] = useState<SubPageHeader | null>(null);
  const page = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const ghost = useRef<HTMLElement | null>(null);
  const depth = useRef(0);
  const subDepth = sub?.depth ?? 0;

  // The section stays on its page while it slides away, and opening one starts it from the top.
  const [shown, setShown] = useState<SettingsSectionId>(section ?? SETTINGS_SECTIONS[0].id);
  const [visit, setVisit] = useState(0);
  const [lastSection, setLastSection] = useState(section);
  if (section !== lastSection) {
    setLastSection(section);
    if (section) {
      setShown(section);
      setVisit(count => count + 1);
    }
  }

  /** Keeps a copy of the page as it looks now, laid exactly over it, to slide away. */
  const prepare = useCallback(() => {
    const live = page.current;
    // Inside the scroller, so the copy shares its layer with the page and the header: the scroller's
    // fade mask would otherwise lift the page beneath anything outside it.
    const host = scroller.current;
    if (!live || !host) return;
    ghost.current?.remove();
    const copy = live.cloneNode(true) as HTMLElement;
    copy.setAttribute("aria-hidden", "true");
    copy.inert = true;
    Object.assign(copy.style, {
      position: "absolute",
      left: "0",
      right: "0",
      top: `${live.offsetTop - host.scrollTop}px`,
    });
    host.appendChild(copy);
    ghost.current = copy;
  }, []);
  const narrowApi = useMemo(() => ({ set: setSub, prepare }), [prepare]);

  // Moving between pages inside a section is the same push as opening the section: the new page
  // comes in from the right over the old one, which slips left and dims; going back reverses it.
  useLayoutEffect(() => {
    const from = depth.current;
    depth.current = subDepth;
    const leaving = ghost.current;
    ghost.current = null;
    if (from === subDepth || !page.current || !leaving) {
      leaving?.remove();
      return;
    }
    scroller.current?.scrollTo({ top: 0 });
    const forward = subDepth > from;
    leaving.style.zIndex = forward ? "0" : "2";
    page.current.animate(forward ? [OFF, ON] : [UNDER, ON], PUSH);
    leaving.animate(forward ? [ON, UNDER] : [ON, OFF], { ...PUSH, fill: "forwards" }).finished.then(
      () => leaving.remove(),
      () => leaving.remove(),
    );
  }, [subDepth]);

  const current = SETTINGS_SECTIONS.find(item => item.id === shown) ?? SETTINGS_SECTIONS[0];

  return (
    <div className="relative h-full overflow-hidden bg-screen">
      <div
        inert={section !== undefined}
        className={cn("absolute inset-0 overflow-y-auto", pushClass)}
        style={section ? UNDER : ON}
      >
        <div className="px-4 pt-6 pb-8">
          <h1 className="px-1 text-2xl font-semibold tracking-tight">You</h1>
          <div className="mt-5 flex flex-col gap-5">
            <Group>
              <Link {...sectionLink("profile")} className={cn(panelRow, "h-auto py-2")}>
                <AccountAvatar
                  name={displayName}
                  image={profile.data?.avatar}
                  size={32}
                  className="shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{displayName}</span>
                  <span className="block truncate text-xs text-muted-foreground">Profile</span>
                </span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />
                <HapticSurface />
              </Link>
            </Group>
            <Group>
              {SETTINGS_SECTIONS.filter(item => item.id !== "profile").map(item => (
                <Link key={item.id} {...sectionLink(item.id)} className={panelRow}>
                  <item.icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {summaries[item.id]}
                  </span>
                  <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />
                  <HapticSurface />
                </Link>
              ))}
            </Group>
            <Group>
              <button type="button" onClick={() => void signOutHere()} className={panelRow}>
                <LogOut aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-left">Sign out</span>
                <HapticSurface />
              </button>
            </Group>
          </div>
        </div>
      </div>

      <div
        inert={section === undefined}
        className={cn("absolute inset-0 bg-screen", pageShadow, pushClass)}
        style={section ? ON : OFF}
      >
        <div ref={scroller} className="scroll-fade h-full overflow-x-hidden overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center gap-3 bg-screen/90 px-3 pt-3 pb-2">
            <button
              type="button"
              onClick={() => {
                if (!sub) {
                  void navigate({ to: "/settings/{-$section}", params: { section: undefined } });
                  return;
                }
                prepare();
                sub.onBack();
              }}
              aria-label="Back"
              className={backChrome}
            >
              <ChevronLeft aria-hidden className="size-4" />
              <HapticSurface />
            </button>
            <h2 className="truncate text-sm font-semibold">{sub?.title ?? current.label}</h2>
          </div>
          <div
            ref={page}
            className={cn(
              "relative z-[1] min-h-full bg-screen pb-24",
              shown === "profile" ? null : "px-4 pt-2",
              pageShadow,
            )}
          >
            <SubPage value={narrowApi}>
              <SectionBody key={`${shown}-${visit}`} id={shown} session={session} phone />
            </SubPage>
          </div>
        </div>
      </div>
    </div>
  );
}
